import { getLocale } from '@/modules/i18n'
import {
  getAuth,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  updateProfile,
  sendPasswordResetEmail,
  confirmPasswordReset,
  GoogleAuthProvider,
  signOut,
  User
} from 'firebase/auth'
import app from '@/modules/firebase'

const auth = getAuth(app)
auth.languageCode = getLocale()

const googleProvider = new GoogleAuthProvider()

export function setAuthObserver(onChange: (user: User | null) => void) {
  onAuthStateChanged(auth, onChange)
}

export async function signUpWithEmail(email: string, password: string) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  )
  return userCredential.user
}

export async function signInWithEmail(email: string, password: string) {
  const userCredential = await signInWithEmailAndPassword(auth, email, password)
  return userCredential.user
}

export async function handleRedirectResult(
  onSuccess: (userData: User, isNewUser: boolean) => Promise<void>
) {
  const redirectResult = await getRedirectResult(auth)

  if (redirectResult) {
    const userInfo = getAdditionalUserInfo(redirectResult)
    const isNewUser = userInfo ? userInfo.isNewUser : false
    await onSuccess(redirectResult.user, isNewUser)
  }
}

export async function signInWithGoogle() {
  return signInWithRedirect(auth, googleProvider)
}

export async function updateUserProfile(
  user: User,
  updateData: { name?: string; photoUrl?: string }
) {
  return updateProfile(user, {
    displayName: updateData.name,
    photoURL: updateData.photoUrl
  })
}

export async function getCurrentUser(): Promise<User | null> {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe()
        resolve(user)
      },
      reject
    )
  })
}

export async function signOutUser() {
  return signOut(auth)
}

export async function triggerPasswordReset(email: string) {
  return sendPasswordResetEmail(auth, email)
}

export async function completePasswordReset(code: string, newPassword: string) {
  return confirmPasswordReset(auth, code, newPassword)
}
